/**
 * DRIFT PACE, and the seam the progression curve arrives through.
 *
 * -- WHAT ROUND 1 GOT WRONG, AND WHAT CHANGED --------------------------------
 *
 * Round 1 paced the sea against a module-local scalar, REFERENCE_PROGRESS_PER_HOUR
 * = 100, while the only progression source in the repository pays thousands of units in
 * the first hour. Nothing failed when the two disagreed, because only one side knew the
 * number existed. THAT CONSTANT IS GONE. It is not re-tuned; the shape that allowed it
 * is removed.
 *
 * The pace is now solved against a PROGRESSION CURVE supplied by whoever owns
 * progression, and the curve carries an identity that travels with it:
 *
 *     leaguesPerProgress  =  sightDistance(first landfall)
 *                            ----------------------------------
 *                            curve.progressAtHours(targetHours)
 *
 *     [leagues] / [prog]  =  [leagues] / [prog]
 *
 * There is no rate in that expression, so there is no rate for the two lanes to
 * disagree about. A world solved against curve A and then DRIVEN by a source realising
 * curve B is refused at the point of use (see resolveVoyage), because the curve id is
 * recorded in the pacing and compared against the source's on every resolve.
 *
 * -- THE DIRECTION OF THE CURVE'S EFFECT, STATED CORRECTLY -------------------
 *
 * Round 1's file claimed the charted span "becomes hundreds of hours only when the real
 * curve replaces the placeholder". THAT IS BACKWARDS, and it was backwards in the
 * direction that flatters the product. An idle curve pays MORE progression per hour as
 * the hours accumulate, so a FIXED span of progression converts to FEWER wall-clock
 * hours under the real curve than under a linear stand-in, never more.
 *
 * pacing.test.ts asserts that direction over generated accelerating curves, so the
 * claim cannot invert again without a test going red.
 *
 * Hundreds of hours therefore has to be bought with WORLD SIZE and CONTENT, and never
 * with the shape of the curve. That is a bill for world-places and progression, and it
 * is stated here rather than left for a reader to discover.
 */

import type { Chart } from './chart';
import type { Leagues, Millis, Progress } from './units';

/**
 * PROGRESSION, as the voyage layer is allowed to see it: a monotone map between hours of
 * play and a lifetime progression scalar, its inverse, and an identity.
 *
 * `id` is load-bearing, not decoration. It is copied into the pacing when a world is
 * solved and compared against the driving source's id on every resolve, which is the
 * whole mechanism that makes a silent mismatch impossible.
 */
export interface ProgressionCurve {
  readonly id: string;
  /**
   * Hours this curve was MEASURED over. Informational: it is a cost or fidelity budget,
   * never a domain.
   *
   * IT USED TO SAY "queries past it clamp, and say so", AND THAT COST A DEAD WORLD.
   * CL4-C-01: a curve whose inverse clamped at 800 h froze the boat at the farthest point
   * of the sea on day 34 of once-a-day play, for ever, with nothing thrown and nothing
   * red. Both directions below are now required to be TOTAL, and an implementation that
   * cannot answer must refuse rather than hand back this number.
   */
  readonly horizonHours: number;
  /**
   * Lifetime progression after `hours` of play from a fresh save. Monotone in hours,
   * UNBOUNDED, and defined for every finite positive hour.
   */
  progressAtHours(hours: number): Progress;
  /**
   * Inverse. Hours of play that this much progression represents. Monotone, UNBOUNDED,
   * and defined for every finite positive progression. `progressAtHours` and this are
   * mutual inverses wherever both are defined.
   */
  hoursAtProgress(progress: Progress): number;
}

export interface VoyagePacing {
  /** THE pace constant. Leagues of track per unit of simulation progression. */
  readonly leaguesPerProgress: number;
  /** The curve the pace was solved against. */
  readonly curve: ProgressionCurve;
  /** Copied off the curve, so a mismatch is comparable data rather than object identity. */
  readonly curveId: string;
  /** The pace target this pacing was solved for, in hours. */
  readonly targetHours: number;
  /** Which target it was: the first landfall, or the whole charted span. */
  readonly targetKind: 'first-landfall' | 'charted-span';
}

export interface PacingTargets {
  /** Hours of drift before the first place other than home comes into sight. */
  readonly firstLandfallHours: number;
  readonly curve: ProgressionCurve;
}

export interface SpanTargets {
  /** Hours of drift from home to the last charted place. */
  readonly chartedSpanHours: number;
  readonly curve: ProgressionCurve;
}

// ---------------------------------------------------------------------------
// The placeholder curve. Named so nobody can ship it by accident.
// ---------------------------------------------------------------------------

/**
 * A straight line, for geometry tests that care about the track and not about time.
 * Its id says exactly what it is. A world solved against it and then driven by the
 * simulation is REFUSED rather than silently mispaced; that refusal is the instrument
 * for round 1's CL1-R1-03, and pace-agreement.test.ts drives it.
 */
export function linearProgressionCurve(progressPerHour: number, horizonHours = 100_000): ProgressionCurve {
  const rate = Number.isFinite(progressPerHour) && progressPerHour > 0 ? progressPerHour : 1;
  return {
    id: `linear-placeholder:${rate}/h`,
    // A straight line is measured everywhere, so this is informational only. It used to
    // be a CLAMP in both directions - the second instance of CL4-C-01's class in this
    // module, at 100,000 h instead of 800 - and a clamp on a line is nothing but a wall.
    horizonHours,
    progressAtHours: (hours) => (Number.isFinite(hours) && hours > 0 ? hours * rate : 0),
    hoursAtProgress: (progress) => (Number.isFinite(progress) && progress > 0 ? progress / rate : 0),
  };
}

/** The stand-in round 1 shipped as its default. Kept only as a labelled test fixture. */
export const LINEAR_PLACEHOLDER_CURVE: ProgressionCurve = linearProgressionCurve(100);

// ---------------------------------------------------------------------------
// Solving the pace.
// ---------------------------------------------------------------------------

/** Sight distance of the first place that is not already in sight at home. */
export function firstLandfallTrackDistance(chart: Chart): Leagues {
  for (const p of chart.places) {
    if (p.sightTrackDistance > 0 && Number.isFinite(p.sightTrackDistance)) return p.sightTrackDistance;
  }
  return Infinity;
}

function solve(
  distance: Leagues,
  curve: ProgressionCurve,
  hours: number,
  targetKind: VoyagePacing['targetKind'],
): VoyagePacing {
  if (!(hours > 0) || !Number.isFinite(hours)) {
    throw new Error('voyage/pacing: pace targets must be positive and finite');
  }
  const progress = curve.progressAtHours(hours);
  if (!(progress > 0) || !Number.isFinite(progress)) {
    throw new Error(`voyage/pacing: curve ${curve.id} pays no progression over ${hours}h`);
  }
  return {
    leaguesPerProgress: distance / progress,
    curve,
    curveId: curve.id,
    targetHours: hours,
    targetKind,
  };
}

export function derivePacingFromFirstLandfall(chart: Chart, targets: PacingTargets): VoyagePacing {
  const d = firstLandfallTrackDistance(chart);
  if (!Number.isFinite(d) || d <= 0) {
    throw new Error('voyage/pacing: chart has no place beyond home to pace against');
  }
  return solve(d, targets.curve, targets.firstLandfallHours, 'first-landfall');
}

export function derivePacingFromChartedSpan(chart: Chart, targets: SpanTargets): VoyagePacing {
  const d = chart.lastSightTrackDistance;
  if (!(d > 0)) throw new Error('voyage/pacing: chart has no charted span to pace against');
  return solve(d, targets.curve, targets.chartedSpanHours, 'charted-span');
}

// ---------------------------------------------------------------------------
// Reading the pace.
// ---------------------------------------------------------------------------

/** Where the boat naturally is, in leagues along the track, at this progression. */
export function trackDistanceAtProgress(progress: Progress, pacing: VoyagePacing): Leagues {
  return Math.max(0, progress) * pacing.leaguesPerProgress;
}

/** Progression at which the boat's natural drift reaches this track distance. */
export function progressAtTrackDistance(distanceLeagues: Leagues, pacing: VoyagePacing): Progress {
  return Math.max(0, distanceLeagues) / pacing.leaguesPerProgress;
}

/** Hours of play this much progression represents, under the pacing's own curve. */
export function modelledHoursAtProgress(progress: Progress, pacing: VoyagePacing): number {
  return pacing.curve.hoursAtProgress(Math.max(0, progress));
}

/**
 * Leagues of track drifted during the hour that BEGINS at `fromHours`.
 *
 * A function of an hour, not a constant, because a real idle curve pays more later.
 * Round 1 published a single leagues-per-hour figure. There is no such number, and a
 * consuming lane that reads one will size the world wrong.
 */
export function driftLeaguesInHour(pacing: VoyagePacing, fromHours: number): Leagues {
  const from = Math.max(0, fromHours);
  const a = pacing.curve.progressAtHours(from);
  const b = pacing.curve.progressAtHours(from + 1);
  return (b - a) * pacing.leaguesPerProgress;
}

/** Modelled hours of drift from home to the last charted place. */
export function chartedSpanModelledHours(chart: Chart, pacing: VoyagePacing): number {
  return modelledHoursAtProgress(progressAtTrackDistance(chart.lastSightTrackDistance, pacing), pacing);
}

// ---------------------------------------------------------------------------
// Curves as drivers.
// ---------------------------------------------------------------------------

export interface CurveBackedSource {
  readonly curveId: string;
  progressAt(timestampMs: Millis): Progress;
  paidHoursAt(timestampMs: Millis): number;
}

/**
 * A progression source that realises a curve from a fixed epoch. It carries the curve's
 * id, so pairing it with a world solved against a different curve is refused instead of
 * quietly mispacing the sea.
 */
export function progressSourceFromCurve(curve: ProgressionCurve, epochMs: Millis): CurveBackedSource {
  const hoursSince = (timestampMs: Millis): number => {
    const h = (timestampMs - epochMs) / 3_600_000;
    return Number.isFinite(h) && h > 0 ? h : 0;
  };
  return {
    curveId: curve.id,
    progressAt: (timestampMs: Millis) => curve.progressAtHours(hoursSince(timestampMs)),
    // A curve-backed stub has no hold to skip and no cap to hit, so every hour since the
    // epoch is a paid hour. That makes it the DEGENERATE case of the real source rather
    // than a different rule, which is what keeps placeholder worlds comparable.
    paidHoursAt: hoursSince,
  };
}
